/**
 * Report Builder - Place Text JavaScript
 *
 * Manages text block placement and WYSIWYG editor initialization
 */

(function () {
  "use strict";

  // Store placed text blocks
  let placedTextBlocks = [];
  let textBlockCounter = 0;
  let isInitialized = false;
  let isAddingTextBlock = false;


  /**
   * Update text block title
   * @param {string} textBlockId - Text block ID
   * @param {string} newTitle - New title
   */
  function updateTextBlockTitle(textBlockId, newTitle) {
    const textBlockConfig = placedTextBlocks.find((t) => t.id === textBlockId);
    if (textBlockConfig) {
      textBlockConfig.customTitle = newTitle;
    }
  }

  /**
   * Initialize the place text functionality
   */
  function initPlaceText() {
    // Prevent multiple initializations
    if (isInitialized) {
      return;
    }

    const addTextBtn = document.getElementById("addTextBtn");

    if (!addTextBtn) {
      // Silently return if button not found (not on report builder page)
      return;
    }

    // Check if event listener already exists by checking for a data attribute
    if (addTextBtn.hasAttribute("data-place-text-initialized")) {
      isInitialized = true;
      return;
    }

    // Mark as initialized BEFORE adding listener to prevent race conditions
    addTextBtn.setAttribute("data-place-text-initialized", "true");
    isInitialized = true;

    // Handle add text button click - use named function for easier removal if needed
    function handleAddTextClick(e) {
      e.preventDefault();
      e.stopPropagation();
      
      // Prevent multiple rapid clicks
      if (isAddingTextBlock) {
        return;
      }
      
      addTextBlockToCanvas();
    }

    addTextBtn.addEventListener("click", handleAddTextClick);
  }

  /**
   * Add text block to canvas
   */
  function addTextBlockToCanvas() {
    // Prevent duplicate calls
    if (isAddingTextBlock) {
      return;
    }
    
    isAddingTextBlock = true;
    // Create unique text block ID
    const textBlockId = `report_text_${++textBlockCounter}_${Date.now()}`;

    // Create text block configuration
    const textBlockConfig = {
      id: textBlockId,
      content: "",
      customTitle: null, // Will default to "Text Block" if null
    };

    // Add to placed text blocks
    placedTextBlocks.push(textBlockConfig);

    // Render text block in canvas
    renderTextBlockInCanvas(textBlockConfig);

    // Hide empty message
    if (window.ReportBuilderUtils?.toggleEmptyMessage) {
      window.ReportBuilderUtils.toggleEmptyMessage(false);
    }
    
    // Reset flag after a short delay to allow the operation to complete
    setTimeout(function() {
      isAddingTextBlock = false;
    }, 100);
  }

  /**
   * Render text block in canvas
   */
  function renderTextBlockInCanvas(textBlockConfig) {
    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (!canvasCharts) {
      return;
    }

    // Create text block container
    const textBlockContainer = document.createElement("div");
    textBlockContainer.className = "canvas-text-item";
    textBlockContainer.id = `text_block_container_${textBlockConfig.id}`;
    textBlockContainer.setAttribute("data-text-block-id", textBlockConfig.id);

    // Create text block header with controls
    const textBlockHeader = document.createElement("div");
    textBlockHeader.className = "text-item-header";
    const displayTitle = textBlockConfig.customTitle || "Text Block";
    
    if (window.ReportBuilderUtils?.generateItemHeaderHtml) {
      textBlockHeader.innerHTML = window.ReportBuilderUtils.generateItemHeaderHtml({
        itemId: textBlockConfig.id,
        displayTitle: displayTitle,
        idAttribute: "data-text-block-id",
        itemType: "text",
        removeButtonClass: "btn-remove-text",
        removeButtonTitle: "Remove text block",
      });
    } else {
      // Fallback if utils not available
      textBlockHeader.innerHTML = `<div class="text-item-title"><h4>${displayTitle}</h4></div>`;
    }

    // Create text block content area with textarea for WYSIWYG
    const textBlockContent = document.createElement("div");
    textBlockContent.className = "text-item-content";
    textBlockContent.id = `text_content_${textBlockConfig.id}`;

    // Create textarea that will be converted to WYSIWYG
    const textarea = document.createElement("textarea");
    textarea.id = `wysiwyg_${textBlockConfig.id}`;
    textarea.className = "wysiwyg-textarea";
    textarea.value = textBlockConfig.content || "";

    textBlockContent.appendChild(textarea);

    // Assemble text block container
    textBlockContainer.appendChild(textBlockHeader);
    textBlockContainer.appendChild(textBlockContent);

    // Add to canvas
    canvasCharts.appendChild(textBlockContainer);

    // Scroll to the new text block
    textBlockContainer.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });

    // Setup move buttons
    if (window.ReportBuilderUtils?.setupMoveButtons) {
      window.ReportBuilderUtils.setupMoveButtons(
        textBlockContainer,
        textBlockConfig.id,
        "data-text-block-id",
        function() {
          updateTextBlockArrayOrder(textBlockConfig.id);
        }
      );
    }

    // Setup edit title button
    if (window.ReportBuilderUtils?.setupEditTitleButton) {
      window.ReportBuilderUtils.setupEditTitleButton(
        textBlockContainer,
        textBlockConfig.id,
        "text",
        function(newTitle) {
          textBlockConfig.customTitle = newTitle;
        }
      );
    }

    // Setup remove button
    const removeBtn = textBlockContainer.querySelector(".btn-remove-text");
    if (removeBtn) {
      removeBtn.addEventListener("click", function () {
        if (window.ReportBuilderUtils?.showConfirmationModal) {
          window.ReportBuilderUtils.showConfirmationModal({
            title: "Remove Text Block",
            message: "Are you sure you want to remove this text block? This action cannot be undone.",
            confirmText: "Remove",
            cancelText: "Cancel",
            onConfirm: function() {
              removeTextBlock(textBlockConfig.id);
            }
          });
        } else {
          // Fallback if modal not available
          if (confirm("Are you sure you want to remove this text block?")) {
            removeTextBlock(textBlockConfig.id);
          }
        }
      });
    }

    // Initialize WYSIWYG editor
    initializeWysiwygEditor(textBlockConfig.id);
  }

  /**
   * Initialize WYSIWYG editor for a text block
   */
  function initializeWysiwygEditor(textBlockId) {
    const editorId = `wysiwyg_${textBlockId}`;
    const textarea = document.getElementById(editorId);

    if (!textarea) {
      console.error("Textarea not found for editor:", editorId);
      return;
    }

    // Check if WordPress editor functions are available
    if (typeof wp === "undefined" || typeof wp.editor === "undefined" || typeof wp.editor.initialize === "undefined") {
      console.error("WordPress editor functions not available");
      // Fallback to regular textarea if WYSIWYG is not available
      textarea.style.width = "100%";
      textarea.style.minHeight = "200px";
      textarea.style.padding = "1rem";
      textarea.style.border = "1px solid #ddd";
      textarea.style.borderRadius = "4px";
      return;
    }

    // Wait a bit for DOM to be ready and editor scripts to load
    setTimeout(function() {
      // Initialize WordPress editor
      // Use wp.editor.initialize for dynamic initialization
      const editorSettings = {
        tinymce: {
          toolbar1: "formatselect,bold,italic,bullist,numlist,blockquote,alignleft,aligncenter,alignright,link,unlink",
          toolbar2: "",
          height: 300,
          wpautop: false,
          block_formats: "Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6;",
          setup: function(editor) {
            editor.on('change', function() {
              editor.save();
            });
          }
        },
        quicktags: true,
        mediaButtons: false,
      };

      // Initialize the editor
      wp.editor.initialize(editorId, editorSettings);

      // Store editor ID for cleanup
      const textBlockConfig = placedTextBlocks.find(
        (t) => t.id === textBlockId
      );
      if (textBlockConfig) {
        textBlockConfig.editorId = editorId;
      }
    }, 100);
  }

  /**
   * Update text block array order to match DOM order
   */
  function updateTextBlockArrayOrder(textBlockId) {
    if (window.ReportBuilderUtils?.updateArrayOrder) {
      window.ReportBuilderUtils.updateArrayOrder(
        placedTextBlocks,
        textBlockId,
        "text_block_container_",
        "data-text-block-id"
      );
    }
  }

  /**
   * Remove text block from canvas
   */
  function removeTextBlock(textBlockId) {
    // Find and remove editor instance
    const textBlockConfig = placedTextBlocks.find((t) => t.id === textBlockId);
    if (textBlockConfig && textBlockConfig.editorId) {
      // Remove TinyMCE instance if it exists
      if (typeof tinymce !== "undefined" && tinymce.get(textBlockConfig.editorId)) {
        tinymce.get(textBlockConfig.editorId).remove();
      }
    }

    // Remove from placed text blocks array
    placedTextBlocks = placedTextBlocks.filter((t) => t.id !== textBlockId);

    // Remove from DOM
    const textBlockContainer = document.getElementById(
      `text_block_container_${textBlockId}`
    );
    if (textBlockContainer) {
      textBlockContainer.remove();
    }

    // Show empty message if no items left
    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (
      canvasCharts &&
      canvasCharts.children.length === 0 &&
      placedTextBlocks.length === 0
    ) {
      if (window.ReportBuilderUtils?.toggleEmptyMessage) {
        window.ReportBuilderUtils.toggleEmptyMessage(true);
      }
    }
  }

  /**
   * Get content from a text block editor
   */
  function getTextBlockContent(textBlockId) {
    const textBlockConfig = placedTextBlocks.find((t) => t.id === textBlockId);
    if (!textBlockConfig || !textBlockConfig.editorId) {
      return "";
    }

    const editorId = textBlockConfig.editorId;

    // Get content from TinyMCE if available
    if (typeof tinymce !== "undefined" && tinymce.get(editorId)) {
      return tinymce.get(editorId).getContent();
    }

    // Fallback to textarea value
    const textarea = document.getElementById(editorId);
    return textarea ? textarea.value : "";
  }

  /**
   * Set content for a text block editor
   */
  function setTextBlockContent(textBlockId, content) {
    const textBlockConfig = placedTextBlocks.find((t) => t.id === textBlockId);
    if (!textBlockConfig || !textBlockConfig.editorId) {
      return;
    }

    const editorId = textBlockConfig.editorId;

    // Set content in TinyMCE if available
    if (typeof tinymce !== "undefined" && tinymce.get(editorId)) {
      tinymce.get(editorId).setContent(content);
      return;
    }

    // Fallback to textarea value
    const textarea = document.getElementById(editorId);
    if (textarea) {
      textarea.value = content;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPlaceText);
  } else {
    initPlaceText();
  }

  // Export functions for potential external use
  window.ReportBuilderText = {
    getTextBlockContent: getTextBlockContent,
    setTextBlockContent: setTextBlockContent,
    removeTextBlock: removeTextBlock,
    updateTextBlockTitle: updateTextBlockTitle,
  };
})();

